import React, { useState } from "react";
import { StyleSheet, ScrollView, View, Alert } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";

export default function DocumentsScreen() {
  const { colors } = useTheme();
  const { session } = useLocalSearchParams();
  const [documents, setDocuments] = useState({
    id: false,
    ssn: false,
    w4: false,
    i9: false,
  });

  const handleUploadDocument = (docType: keyof typeof documents) => {
    // TODO: Implement document upload
    Alert.alert("Document Upload", `Upload ${docType.toUpperCase()} document`);
    setDocuments((prev) => ({ ...prev, [docType]: true }));
  };

  const allDocsUploaded = Object.values(documents).every(Boolean);

  const documentTypes = [
    { key: "id", label: "Government ID", description: "Driver's license or passport", icon: "credit-card" },
    { key: "ssn", label: "Social Security Card", description: "Or tax document with SSN", icon: "security" },
    { key: "w4", label: "W-4 Form", description: "Tax withholding form", icon: "description" },
    { key: "i9", label: "I-9 Form", description: "Employment eligibility verification", icon: "verified-user" },
  ] as const;

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <ThemedText style={[styles.title, { color: colors.text }]}>Required Documents</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          Upload the following documents to complete your registration
        </ThemedText>

        <View style={styles.documentsList}>
          {documentTypes.map(({ key, label, description, icon }) => (
            <View
              key={key}
              style={[
                styles.documentCard,
                {
                  backgroundColor: colors.card,
                  borderColor: colors.border,
                  borderWidth: 1,
                },
              ]}
            >
              <View style={styles.documentInfo}>
                <View style={styles.documentHeader}>
                  <MaterialIcons
                    name={icon as any}
                    size={24}
                    color={documents[key] ? colors.emerald?.text || colors.primary : colors.placeholder}
                  />
                  <View style={styles.documentText}>
                    <ThemedText style={[styles.documentLabel, { color: colors.text }]}>{label}</ThemedText>
                    <ThemedText style={[styles.documentDescription, { color: colors.textSecondary }]}>
                      {description}
                    </ThemedText>
                  </View>
                </View>

                {documents[key] ? (
                  <View
                    style={[
                      styles.statusBadge,
                      { backgroundColor: colors.emerald?.background || colors.primary + "20" },
                    ]}
                  >
                    <MaterialIcons name="check-circle" size={16} color={colors.emerald?.text || colors.primary} />
                    <ThemedText style={[styles.statusText, { color: colors.emerald?.text || colors.primary }]}>
                      Uploaded
                    </ThemedText>
                  </View>
                ) : (
                  <Button
                    text="Upload"
                    iconPosition="right"
                    onPress={() => handleUploadDocument(key)}
                    icon="cloud-upload"
                    iconSize={16}
                    style={[styles.uploadButton, { backgroundColor: colors.primary }]}
                    textStyle={{ color: "white", fontSize: 14 }}
                  />
                )}
              </View>
            </View>
          ))}
        </View>

        <Button
          text="Continue"
          onPress={() => router.push(`/sign-up/${session}/photo`)}
          icon="arrow-right"
          iconPosition="right"
          iconSize={18}
          iconColor="white"
          style={[
            styles.continueButton,
            {
              backgroundColor: allDocsUploaded ? colors.primary : colors.border,
              opacity: allDocsUploaded ? 1 : 0.5,
            },
          ]}
          textStyle={{ color: "white", fontSize: 16 }}
          enabled={allDocsUploaded}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
  },
  documentsList: {
    gap: 16,
    marginBottom: 32,
  },
  documentCard: {
    borderRadius: 12,
    padding: 16,
  },
  documentInfo: {
    gap: 12,
  },
  documentHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  documentText: {
    flex: 1,
  },
  documentLabel: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  documentDescription: {
    fontSize: 14,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600",
  },
  uploadButton: {
    alignSelf: "flex-start",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  continueButton: {
    borderRadius: 8,
    minHeight: 48,
  },
});
